
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditQueueDialog } from "@/components/EditQueueDialog";
import { SubscriptionDialog } from "@/components/SubscriptionDialog";
import { Queue } from "@/types/queue";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { QueueList } from "@/components/QueueList";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProfileData } from "@/hooks/useProfileData";
import { SettingsButton } from "@/components/SettingsButton";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useQueues } from "@/hooks/useQueues";

const Index = () => {
  const [editingQueue, setEditingQueue] = useState<Queue | null>(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { queues, fetchQueues } = useQueues();
  const { companyName, subscription, isLoading } = useProfileData();

  const handleCreateQueue = async (newQueue: Omit<Queue, "id">) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      const { error } = await supabase
        .from('queues')
        .insert([{
          ...newQueue,
          user_id: user.id,
        }]);

      if (error) {
        throw error;
      }

      await fetchQueues();
      toast({
        title: "Queue created",
        description: `${newQueue.name} has been created successfully.`,
      });
    } catch (error: any) {
      console.error('Error creating queue:', error);
      throw error;
    }
  };

  const handleEditQueue = async (updatedQueue: Queue) => {
    try {
      const { error } = await supabase
        .from('queues')
        .update({
          name: updatedQueue.name,
          current_number: updatedQueue.current_number,
          people_in_queue: updatedQueue.people_in_queue,
          avg_wait_time: updatedQueue.avg_wait_time,
          description: updatedQueue.description,
          last_updated: new Date().toISOString(),
        })
        .eq('id', updatedQueue.id);

      if (error) throw error;

      await fetchQueues();
      toast({
        title: "Queue updated",
        description: `${updatedQueue.name} has been updated successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Error updating queue",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteQueue = async (queue: Queue) => {
    try {
      const { error } = await supabase
        .from('queues')
        .delete()
        .eq('id', queue.id);

      if (error) throw error;

      await fetchQueues();
      toast({
        title: "Queue deleted",
        description: `${queue.name} has been deleted successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Error deleting queue",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/login", { replace: true });
    } catch (error: any) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <div className="container space-y-8 px-4 py-8">
        {!isLoading && !subscription && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Your account requires an active subscription</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowSubscriptionDialog(true)}
              >
                Choose a Plan
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Header 
          companyName={companyName} 
          onSignOut={handleSignOut}
          onCreateQueue={handleCreateQueue}
        />
        <QueueList
          queues={queues}
          onEdit={setEditingQueue}
          onClick={(queue) => navigate(`/queue/${queue.id}`)}
          onDelete={handleDeleteQueue}
          onCreateQueue={handleCreateQueue}
        />
        <EditQueueDialog
          queue={editingQueue}
          open={!!editingQueue}
          onOpenChange={(open) => !open && setEditingQueue(null)}
          onEditQueue={handleEditQueue}
        />
        <SubscriptionDialog
          open={showSubscriptionDialog}
          onOpenChange={setShowSubscriptionDialog}
          mode="subscribe"
        />
        <SettingsButton />
      </div>
    </div>
  );
};

export default Index;
